import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';

import { Flex, Box } from 'rebass';

import { FirstUpper, ManageAllButton, GlowHeading } from 'components';
import { T, PayingCustomer } from 'containers';

import {
  getTitle,
  getArrivalDateTime,
  getDepartureDateTime,
} from 'helpers/reservation';
import { AppText } from 'domain/appText';

const Cell = styled.td`
  padding: 0 0px 0 0;
  vertical-align: top;
`;

const StyledBox = styled(Box)`
  text-align: right;
`;

const StyledGlowHeading = styled(GlowHeading)`
  margin-bottom: 10px;
  
   &.sticky {
    position: fixed!important;
    left: 125px;
    top: 20px;
    z-index: 200;
  }
`;

const StyledTable = styled.table`
  tr td:first-child{
    padding-right: 10px;
  }
`;

const StyledGroup = styled.div`
  padding: 0px;
  width: 100%;

  td{
    img{
      width: 14px;
      height: 14px;
      vertical-align: top;
      display: ${({ valid }) => valid ? 'inline-block' : 'none'};
    }
    span{
      display: ${({ valid }) => valid ? 'none' : 'inline'};
    }
  }

  ${({ valid }) => valid ? '' : 'color: #e86f71;'}
`;

const GuestDiv = styled.div`
  border: 1px solid #dde6f0;
  padding-bottom: 8px;
  display: flex;
  flex-flow: row wrap;

  ${GlowHeading}{
    padding: 10px 18px;
    background-color: #ecf2f8;
    margin-bottom: 10px;
    line-height: 1em;
    font-size: 14px;
    display: block;
    width: 100%;
  }

  .age-category-holder{
    padding: 5px 10px;
    width: calc(100% - 20px);

    table{
      padding: 0px 10px;
      width: 100%;
      line-height: 17px;

      td:last-child{
        width: 60px;
        text-align: right;
      }
    }

  }


`;

const AgeGroup = ({ title, actual, expected, customerCategories }) => (
  actual + expected === 0 ? null : (<StyledGroup
    valid={actual === expected}
    title={actual !== expected ? `${expected} ${title} ${customerCategories} ${actual}` : null}
  >
    <table>
      <tbody>
        <tr>
          <Cell><FirstUpper bold>{title}:</FirstUpper></Cell>
          <Cell>{actual} <span>({expected})</span><img src="/assets/images/ok.svg" alt="Ok" /></Cell>
        </tr>
      </tbody>
    </table>
  </StyledGroup>
));
AgeGroup.propTypes = {
  title: PropTypes.string.isRequired,
  actual: PropTypes.number.isRequired,
  expected: PropTypes.number.isRequired,
  customerCategories: PropTypes.string.isRequired,
};


const GuestListHeading = ({
  departure,
  adults,
  expectedAdults,
  childs,
  expectedChilds,
  infants,
  expectedInfants,
  students,
  expectedStudents,
  seniors,
  expectedSeniors,
  onManageAll,
  hideCabinButton,
}) => {
  const departureDateTime = getDepartureDateTime(departure);
  const arrivalDateTime = getArrivalDateTime(departure);

  return (
    <T
      text={[
        AppText.Guests,
        AppText.Departure,
        AppText.Arrival,
        AppText.Adults,
        AppText.Children,
        AppText.Infants,
        AppText.Students,
        AppText.Seniors,
        AppText.PayingCustomer,
        AppText.CustomerCategoriesValidation,
      ]}
      render={([
        guestsText,
        departureText,
        arrivalText,
        adultsText,
        childrenText,
        infantsText,
        studentsText,
        seniorsText,
        payCustomer,
        customerCategoriesValidation,
      ]) => (
        <Flex align="center" py={5}>
          <Box width={19 / 48}>
            <Box w={1} pl={5} pr={2}>
              <StyledGlowHeading className="glow-heading" level={3} f={3}>{getTitle(departure)}</StyledGlowHeading>
              <StyledTable>
                <tbody>
                  {departureDateTime && <tr><Cell><FirstUpper bold>{departureText}:</FirstUpper></Cell><Cell><FirstUpper>{moment(departureDateTime).format('LLLL')}</FirstUpper></Cell></tr>}
                  {arrivalDateTime && <tr><Cell><FirstUpper bold>{arrivalText}:</FirstUpper></Cell><Cell><FirstUpper>{moment(arrivalDateTime).format('LLLL')}</FirstUpper></Cell></tr>}
                  <tr><td>&nbsp;</td></tr>
                  <tr><Cell><FirstUpper bold>{payCustomer}:</FirstUpper></Cell><Cell><PayingCustomer /></Cell></tr>
                </tbody>
              </StyledTable>
            </Box>
          </Box>
          <Box width={10 / 48} p={0}>
            <GuestDiv>
              <GlowHeading level={4} f={2}><FirstUpper>{guestsText}</FirstUpper></GlowHeading>
              <div className="age-category-holder">
                <AgeGroup title={adultsText} actual={adults} expected={expectedAdults} customerCategories={customerCategoriesValidation} />
                <AgeGroup title={childrenText} actual={childs} expected={expectedChilds} customerCategories={customerCategoriesValidation} />
                <AgeGroup title={infantsText} actual={infants} expected={expectedInfants} customerCategories={customerCategoriesValidation} />
                <AgeGroup title={studentsText} actual={students} expected={expectedStudents} customerCategories={customerCategoriesValidation} />
                <AgeGroup title={seniorsText} actual={seniors} expected={expectedSeniors} customerCategories={customerCategoriesValidation} />
              </div>
            </GuestDiv>
          </Box>
          <Box width={8 / 48} />
          <StyledBox width={11 / 48}>
            {!hideCabinButton && <ManageAllButton onClick={onManageAll} />}
          </StyledBox>
        </Flex>
      )}
    />
  );
};
GuestListHeading.propTypes = {
  departure: PropTypes.object,
  adults: PropTypes.number,
  expectedAdults: PropTypes.number,
  childs: PropTypes.number,
  expectedChilds: PropTypes.number,
  infants: PropTypes.number,
  expectedInfants: PropTypes.number,
  students: PropTypes.number,
  expectedStudents: PropTypes.number,
  seniors: PropTypes.number,
  expectedSeniors: PropTypes.number,
  payingCustomer: PropTypes.shape(PayingCustomer.propTypes),
  onManageAll: PropTypes.func,
  hideCabinButton: PropTypes.bool,
};
GuestListHeading.defaultProps = {
  departure: {},
  adults: 0,
  expectedAdults: 0,
  childs: 0,
  expectedChilds: 0,
  infants: 0,
  expectedInfants: 0,
  students: 0,
  expectedStudents: 0,
  seniors: 0,
  expectedSeniors: 0,
  payingCustomer: {},
  onManageAll: () => {},
};

export default GuestListHeading;



// WEBPACK FOOTER //
// ./src/components/organisms/GuestListHeading/index.js